/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import type { ChunkStore, ChunkWriter } from "@tivocorp/sage-web-sdk/api";

/**
 * In-memory fallback implementation of {@link ChunkStore}.
 *
 * Used when IndexedDB is unavailable. Chunks are stored in a Map and are lost
 * on page reload. When used for diagnostics, pass a reduced maxChunkCount to
 * SageLoggerImpl to avoid excessive memory use (default: 500 × 10 KB = 5 MB).
 */
export class InMemoryChunkStore implements ChunkStore {
  private readonly chunks = new Map<string, Uint8Array>();

  openWriter(chunkId: string): ChunkWriter {
    return new InMemoryChunkWriter((data) => {
      this.chunks.set(chunkId, data);
    });
  }

  async read(chunkId: string): Promise<Uint8Array | null> {
    return this.chunks.get(chunkId) || null;
  }

  async delete(chunkId: string): Promise<void> {
    this.chunks.delete(chunkId);
  }

  async list(): Promise<string[]> {
    return Array.from(this.chunks.keys());
  }
}

class InMemoryChunkWriter implements ChunkWriter {
  private parts: Uint8Array[] = [];
  private _size = 0;
  private readonly onFlush: (data: Uint8Array) => void;

  constructor(onFlush: (data: Uint8Array) => void) {
    this.onFlush = onFlush;
  }

  get size(): number {
    return this._size;
  }

  async append(data: Uint8Array): Promise<void> {
    this.parts.push(data);
    this._size += data.byteLength;
    this.onFlush(this.concat());
  }

  async close(): Promise<void> {
    // no-op: no resources to release
  }

  private concat(): Uint8Array {
    if (this.parts.length === 1) return this.parts[0];
    const result = new Uint8Array(this._size);
    let offset = 0;
    for (const part of this.parts) {
      result.set(part, offset);
      offset += part.byteLength;
    }
    this.parts = [result];
    return result;
  }
}
